import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { aiAssistantActions } from '../../store/aiAssistantSlice';
import { selectError, selectMessages } from '../../store/selectors';
import { ChatTopBar } from './ChatTopBar';
import { ChatMessageList } from './ChatMessageList';
import { ChatInput } from './ChatInput';
import type { RoadmapContext } from '../../exposes/AIAssistant';
import './AIAssistant.scss';

export interface AIAssistantProps {
  /** embedded = slideout section; overlay = timeline right-side dock */
  mode: 'embedded' | 'overlay';
  context?: RoadmapContext;
  isOpen?: boolean;
  onClose?: () => void;
}

export function AIAssistant({
  mode,
  context,
  isOpen = true,
  onClose
}: AIAssistantProps) {
  const dispatch = useDispatch();
  const error = useSelector(selectError);
  const messages = useSelector(selectMessages);
  const hasMessages = messages.length > 0;
  const chatAreaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dispatch(aiAssistantActions.setExternalContext(context ?? {}));
  }, [context, dispatch]);

  if (mode === 'embedded' && !isOpen) {
    return null;
  }

  return (
    <div
      className={[
        'rau-container',
        mode === 'overlay'
          ? 'rau-container--standalone'
          : 'rau-container--embedded',
        hasMessages ? 'rau-container--conversation' : 'rau-container--empty'
      ].join(' ')}
    >
      <div
        ref={chatAreaRef}
        className={[
          'rau-chat-area',
          hasMessages ? 'rau-chat-area--conversation' : 'rau-chat-area--empty'
        ]
          .join(' ')
          .trim()}
      >
        {!hasMessages && mode === 'overlay' && onClose && (
          <div className="rau-empty-close-row">
            <button
              type="button"
              className="rau-empty-close"
              onClick={onClose}
              aria-label="Close"
            >
              <span className="rau-empty-close-inner" aria-hidden="true">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.5 3.5L12.5 12.5M12.5 3.5L3.5 12.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            </button>
          </div>
        )}
        {hasMessages ? (
          <>
            <ChatTopBar onClose={mode === 'overlay' ? onClose : undefined} />
            <ChatMessageList
              scrollContainerRef={chatAreaRef}
              emptyLayout={mode === 'embedded' ? 'embedded' : 'overlay'}
            />
          </>
        ) : (
          <div className="rau-empty-center">
            <ChatMessageList
              emptyLayout={mode === 'embedded' ? 'embedded' : 'overlay'}
            />
          </div>
        )}
      </div>
      {error && <div className="rau-error">{error}</div>}
      {hasMessages && <ChatInput />}
    </div>
  );
}
